import { ToolValidationDetails } from "@app/components/assistant/conversation/ToolValidationDetails";
import { getIcon } from "@app/components/resources/resources_icons";
import type { MCPValidationOutputType } from "@app/lib/actions/constants";
import type { BlockedToolExecution } from "@app/lib/actions/mcp";
import {
  EDIT_INFORMATION_TOOL_NAME,
  POD_MANAGER_SERVER_NAME,
  UPDATE_MEMBERS_TOOL_NAME,
} from "@app/lib/api/actions/servers/pod_manager/metadata";
import {
  isPodManagerEditInformationInput,
  isPodManagerUpdateMembersInput,
} from "@app/lib/api/actions/servers/pod_manager/types";
import {
  CREATE_TASKS_TOOL_NAME,
  POD_TASKS_SERVER_NAME,
  UPDATE_TASKS_TOOL_NAME,
} from "@app/lib/api/actions/servers/pod_tasks/metadata";
import {
  isPodTasksCreateTasksInput,
  isPodTasksUpdateTasksInput,
} from "@app/lib/api/actions/servers/pod_tasks/types";
import { WAKEUPS_SERVER_NAME } from "@app/lib/api/actions/servers/wakeups/metadata";
import { canCurrentUserRespondToParentUserMessage } from "@app/lib/api/assistant/conversation/can_current_user_respond";
import { useAuth } from "@app/lib/auth/AuthContext";
import { asDisplayName } from "@app/types/shared/utils/string_utils";
import type { LightWorkspaceType, UserType } from "@app/types/user";
import {
  Button,
  Check,
  Checkbox,
  ContentMessage,
  Label,
  XClose,
} from "@dust-tt/sparkle";
import i18n from "i18next";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

type ToolOverride = {
  title?: (inputs: Record<string, unknown>) => string;
  alwaysAllowLabel?: (inputs: Record<string, unknown>) => string;
  detailsExpanded?: boolean;
};

/** Overrides title, alwaysAllowLabel, and details expansion for specific MCP tools */
const MCP_TOOL_OVERRIDES: Partial<
  Record<string, Partial<Record<string, ToolOverride>>>
> = {
  "dust-chrome-extension": {
    interact_with_page: {
      title: (inputs) =>
        i18n.t(
          "conv.mcpAllowAgentInteractWithPage",
          "Allow agent to {{description}}?",
          { description: inputs.humanReadableDescription }
        ),
      alwaysAllowLabel: () =>
        i18n.t(
          "conv.mcpAllowAllInteractions",
          "Allow all the interactions with this tab"
        ),
    },
  },
  "dust-firefox-extension": {
    interact_with_page: {
      title: (inputs) =>
        i18n.t(
          "conv.mcpAllowAgentInteractWithPage",
          "Allow agent to {{description}}?",
          { description: inputs.humanReadableDescription }
        ),
      alwaysAllowLabel: () =>
        i18n.t(
          "conv.mcpAllowAllInteractions",
          "Allow all the interactions with this tab"
        ),
    },
  },
  sandbox: {
    add_egress_domain: {
      title: () =>
        i18n.t(
          "conv.mcpAllowAgentAddEgressDomain",
          "Allow agent to add a domain to the Computer?"
        ),
      detailsExpanded: true,
    },
  },
  [POD_TASKS_SERVER_NAME]: {
    [CREATE_TASKS_TOOL_NAME]: {
      title: (inputs) => {
        if (!isPodTasksCreateTasksInput(inputs)) {
          return i18n.t(
            "conv.mcpAllowAgentCreateTasksDefault",
            "Allow agent to create tasks?"
          );
        }
        const count = inputs.tasks.length;
        return i18n.t("conv.mcpAllowAgentCreateTasksCount", {
          count,
          defaultValue_one: "Allow agent to create {{count}} task?",
          defaultValue_other: "Allow agent to create {{count}} tasks?",
        });
      },
      alwaysAllowLabel: () =>
        i18n.t(
          "conv.mcpAlwaysAllowAgentCreateTasks",
          "Always allow agent to create tasks"
        ),
    },
    [UPDATE_TASKS_TOOL_NAME]: {
      title: (inputs) => {
        if (!isPodTasksUpdateTasksInput(inputs)) {
          return i18n.t(
            "conv.mcpAllowAgentUpdateTasksDefault",
            "Allow agent to update tasks?"
          );
        }
        const count = inputs.tasks.length;
        const doneCount = inputs.tasks.filter(
          (task) => task.doneRationale
        ).length;
        if (doneCount > 0 && doneCount === count) {
          return i18n.t("conv.mcpAllowAgentMarkTasksDone", {
            count,
            defaultValue_one: "Allow agent to mark {{count}} task as done?",
            defaultValue_other: "Allow agent to mark {{count}} tasks as done?",
          });
        }
        if (doneCount > 0) {
          return i18n.t("conv.mcpAllowAgentUpdateTasksWithDone", {
            count,
            doneCount,
            defaultValue_one:
              "Allow agent to update {{count}} task ({{doneCount}} marked as done)?",
            defaultValue_other:
              "Allow agent to update {{count}} tasks ({{doneCount}} marked as done)?",
          });
        }
        return i18n.t("conv.mcpAllowAgentUpdateTasksCount", {
          count,
          defaultValue_one: "Allow agent to update {{count}} task?",
          defaultValue_other: "Allow agent to update {{count}} tasks?",
        });
      },
      alwaysAllowLabel: () =>
        i18n.t(
          "conv.mcpAlwaysAllowAgentUpdateTasks",
          "Always allow agent to update tasks"
        ),
    },
  },
  [POD_MANAGER_SERVER_NAME]: {
    [EDIT_INFORMATION_TOOL_NAME]: {
      title: (inputs) => {
        if (!isPodManagerEditInformationInput(inputs)) {
          return i18n.t(
            "conv.mcpAllowAgentEditPodInformation",
            "Allow agent to edit Pod information?"
          );
        }
        const fields: string[] = [];
        if (inputs.title !== undefined) {
          fields.push(i18n.t("conv.mcpPodFieldTitle", "title"));
        }
        if (inputs.description !== undefined) {
          fields.push(i18n.t("conv.mcpPodFieldDescription", "description"));
        }
        if (inputs.access !== undefined) {
          fields.push(i18n.t("conv.mcpPodFieldAccess", "access"));
        }
        if (inputs.pinnedFramePath !== undefined) {
          fields.push(i18n.t("conv.mcpPodFieldPinnedFrame", "pinned frame"));
        }
        if (fields.length === 0) {
          return i18n.t(
            "conv.mcpAllowAgentEditPodInformation",
            "Allow agent to edit Pod information?"
          );
        }
        return i18n.t(
          "conv.mcpAllowAgentUpdatePodFields",
          "Allow agent to update Pod {{fields}}?",
          { fields: fields.join(", ") }
        );
      },
      alwaysAllowLabel: () =>
        i18n.t(
          "conv.mcpAlwaysAllowAgentEditPodInformation",
          "Always allow agent to edit Pod information"
        ),
    },
    [UPDATE_MEMBERS_TOOL_NAME]: {
      title: (inputs) => {
        if (!isPodManagerUpdateMembersInput(inputs)) {
          return i18n.t(
            "conv.mcpAllowAgentUpdatePodMembersDefault",
            "Allow agent to update Pod members?"
          );
        }
        const addCount = Object.keys(inputs.membersToAdd ?? {}).length;
        const removeCount = inputs.membersToRemove?.length ?? 0;
        const parts: string[] = [];
        if (addCount > 0) {
          parts.push(
            i18n.t("conv.mcpAddCountLabel", "add {{count}}", {
              count: addCount,
            })
          );
        }
        if (removeCount > 0) {
          parts.push(
            i18n.t("conv.mcpRemoveCountLabel", "remove {{count}}", {
              count: removeCount,
            })
          );
        }
        return i18n.t("conv.mcpAllowAgentUpdatePodMembers", {
          count: addCount + removeCount,
          parts: parts.join(` ${i18n.t("conv.mcpAndLabel", "and")} `),
          defaultValue_one: "Allow agent to {{parts}} Pod user?",
          defaultValue_other: "Allow agent to {{parts}} Pod users?",
        });
      },
      alwaysAllowLabel: () =>
        i18n.t(
          "conv.mcpAlwaysAllowAgentUpdatePodMembers",
          "Always allow agent to update Pod members"
        ),
    },
  },
  [WAKEUPS_SERVER_NAME]: {
    schedule_wakeup: {
      title: () =>
        i18n.t(
          "conv.mcpAllowAgentScheduleWakeup",
          "Allow agent to schedule a wake-up?"
        ),
    },
    list_wakeups: {
      title: () =>
        i18n.t(
          "conv.mcpAllowAgentListWakeups",
          "Allow agent to list wake-ups?"
        ),
    },
    cancel_wakeup: {
      title: () =>
        i18n.t(
          "conv.mcpAllowAgentCancelWakeup",
          "Allow agent to cancel a wake-up?"
        ),
    },
  },
};

// Display data needed to render a tool validation card, for both agent-loop and sandbox-function
// blocked tool executions.
export type ToolValidationRequest = Pick<
  BlockedToolExecution,
  | "actionId"
  | "userId"
  | "stake"
  | "inputs"
  | "metadata"
  | "approvalArgsLabel"
  | "argumentsRequiringApproval"
>;

interface ToolValidationCardProps {
  validationRequest: ToolValidationRequest;
  triggeringUser: UserType | null;
  owner: LightWorkspaceType;
  conversationId?: string | null;
  errorMessage: string | null;
  isValidating: boolean;
  isPulsing?: boolean;
  // Submits the user's decision; returns whether the submission succeeded.
  onValidate: (approved: MCPValidationOutputType) => Promise<boolean>;
}

export function ToolValidationCard({
  validationRequest,
  triggeringUser,
  owner,
  conversationId,
  errorMessage,
  isValidating,
  isPulsing = false,
  onValidate,
}: ToolValidationCardProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [neverAskAgain, setNeverAskAgain] = useState(false);

  const canCurrentUserRespond = useMemo(
    () =>
      canCurrentUserRespondToParentUserMessage({
        parentUserId: validationRequest.userId,
        currentUserId: user?.sId,
      }),
    [validationRequest.userId, user?.sId]
  );

  const icon = validationRequest.metadata.icon
    ? getIcon(validationRequest.metadata.icon)
    : undefined;

  const handleValidation = async (approved: "approved" | "rejected") => {
    const success = await onValidate(
      approved === "approved" && neverAskAgain ? "always_approved" : approved
    );
    if (success) {
      setNeverAskAgain(false);
    }
  };

  const toolOverride =
    MCP_TOOL_OVERRIDES[validationRequest.metadata.mcpServerName]?.[
      validationRequest.metadata.toolName
    ];

  function getTitle() {
    if (!canCurrentUserRespond) {
      return t(
        "conv.mcpPermissionNeededFor",
        "Permission needed for {{name}}.",
        {
          name: asDisplayName(validationRequest.metadata.mcpServerName),
        }
      );
    }
    if (toolOverride?.title) {
      return toolOverride.title(validationRequest.inputs);
    }
    const subject =
      validationRequest.metadata.displayedAs === "agent"
        ? "agent"
        : validationRequest.metadata.mcpServerName;
    return t("conv.mcpAllowToolDefault", "Allow {{subject}} to {{action}}?", {
      subject: asDisplayName(subject),
      action: asDisplayName(validationRequest.metadata.toolName),
    });
  }

  function getAlwaysAllowLabel() {
    if (validationRequest.stake !== "medium") {
      return t("conv.mcpAlwaysAllow", "Always allow");
    }
    if (toolOverride?.alwaysAllowLabel) {
      return toolOverride.alwaysAllowLabel(validationRequest.inputs);
    }

    if (validationRequest.approvalArgsLabel) {
      return validationRequest.approvalArgsLabel;
    }
    const args = validationRequest.argumentsRequiringApproval ?? [];
    const argValues = args
      .filter((arg) => validationRequest.inputs[arg] != null)
      .map((arg) => {
        const value = validationRequest.inputs[arg];
        if (Array.isArray(value)) {
          return value.map(String).join(", ");
        }
        return JSON.stringify(value);
      });
    return argValues.length > 0
      ? t(
          "conv.mcpAlwaysAllowAgentToolWithParams",
          "Always allow agent to {{action}} for the following parameters: {{params}}",
          {
            action: asDisplayName(validationRequest.metadata.toolName),
            params: argValues.join(", "),
          }
        )
      : t("conv.mcpAlwaysAllowAgentTool", "Always allow agent to {{action}} ", {
          action: asDisplayName(validationRequest.metadata.toolName),
        });
  }

  const title = getTitle();
  const alwaysAllowLabel = getAlwaysAllowLabel();

  return (
    <ContentMessage
      title={title}
      variant="primary"
      className="flex w-full flex-col gap-3 sm:w-80 sm:min-w-[500px]"
      icon={icon}
    >
      {canCurrentUserRespond ? (
        <>
          <ToolValidationDetails
            blockedAction={validationRequest}
            user={user}
            owner={owner}
            conversationId={conversationId}
            defaultExpanded={toolOverride?.detailsExpanded}
          />
          {errorMessage && (
            <div className="mt-2 text-sm font-medium text-warning-800">
              {errorMessage}
            </div>
          )}
          <div className="flex flex-col gap-3 sm:mt-3">
            {(validationRequest.stake === "low" ||
              validationRequest.stake === "medium") && (
              <Label
                htmlFor="never-ask-again"
                className="flex w-fit cursor-pointer flex-row items-center gap-2 py-1 pr-2 text-xs"
              >
                <Checkbox
                  id="never-ask-again"
                  checked={neverAskAgain}
                  onCheckedChange={(check) => {
                    setNeverAskAgain(!!check);
                  }}
                />
                <span className="text-normal font-normal">
                  {alwaysAllowLabel}
                </span>
              </Label>
            )}
            <div className="hidden sm:block sm:flex-grow" />
            <div className="flex flex-row gap-3 self-end">
              <Button
                label={t("conv.decline", "Decline")}
                variant="outline"
                size="xs"
                icon={XClose}
                disabled={isValidating}
                isPulsing={isPulsing}
                onClick={() => void handleValidation("rejected")}
              />
              <Button
                label={t("conv.allow", "Allow")}
                variant="highlight"
                size="xs"
                icon={Check}
                disabled={isValidating}
                isPulsing={isPulsing}
                onClick={() => void handleValidation("approved")}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="font-sm whitespace-normal break-words text-foreground">
          <Trans
            i18nKey="conv.mcpWaitingForUserToConfirmBold"
            defaults="Waiting for <0>{{name}}</0> to confirm."
            values={{ name: triggeringUser?.fullName ?? "" }}
            components={[<span key="0" className="font-semibold" />]}
          />
        </div>
      )}
    </ContentMessage>
  );
}
